import httpContext from '@rover/rsdk/src/modules/Network/httpContext';
import type { DataLayerEvent } from '@rover/types/src/DataLayerEvent';
import { Http301Error } from '@rover/utilities/HttpError';

export { default as getQueryParams } from '@rover/utilities/getQueryParams';

export const addEventParamsToUrl = (
  url: string,
  gtmEventParameters: Array<{ [key: string]: string }>
): string => {
  let newUrl = url;

  if (gtmEventParameters) {
    for (let i = 0; i < gtmEventParameters.length; i += 1) {
      const params = gtmEventParameters[i];
      let paramsString = '';

      if (params) {
        Object.keys(params).forEach((key) => {
          const param = params[key];
          if (Array.isArray(param)) {
            const arr: string[] = param;

            if (arr.length) {
              arr.forEach((val) => {
                paramsString += paramsString ? '&' : '';
                paramsString += `${key}[]=${val}`;
              });
            } else {
              paramsString += paramsString ? '&' : '';
              paramsString += `bepEmptyList=${key}`;
            }
          } else {
            paramsString += paramsString ? '&' : '';
            paramsString += `${key}=${params[key]}`;
          }
        });

        if (paramsString) {
          newUrl += `${newUrl.split('?')[1] ? '&' : '?'}bep=${encodeURIComponent(paramsString)}`;
        }
      }
    }
  }

  return newUrl;
};

type BepParams = { [key: string]: string | string[] };

const BEP_LIST_SUFFIX = '[]';
const BEP_EMPTY_LIST_KEY = 'bepEmptyList';

// Each `bep` value is itself a URL-encoded query string holding one event's params.
// `id[]=1&id[]=2` decodes to { id: ['1', '2'] }, and `bepEmptyList=id` to { id: [] }.
const decodeInnerBepParameters = (bep: string): BepParams => {
  const params: BepParams = {};

  new URLSearchParams(bep).forEach((value, key) => {
    if (key === BEP_EMPTY_LIST_KEY) {
      params[value] = [];
    } else if (key.endsWith(BEP_LIST_SUFFIX)) {
      const listKey = key.slice(0, -2);
      params[listKey] = [...((params[listKey] as string[] | undefined) ?? []), value];
    } else {
      params[key] = value;
    }
  });

  return params;
};

// Inverse of addEventParamsToUrl, matching BackendEventsMiddleware (analytics/middleware.py).
export const getEventParamsFromUrl = (search: string): DataLayerEvent[] =>
  new URLSearchParams(search).getAll('bep').flatMap((bep) => {
    const { event, ...rest } = decodeInnerBepParameters(bep);
    // addEventParamsToUrl serializes a missing event name as the string 'undefined'.
    if (typeof event !== 'string' || event === 'undefined') return [];
    return [{ event, ...rest }];
  });

export function redirect(url: URL | string): void {
  const logger = httpContext.getLogger();
  const res = httpContext.getRes();

  if (res?.headersSent) {
    logger?.warn(
      `Attempted to call redirect("${url}") when headers were already sent.` +
        ` Are you trying to redirect twice in the same response?`
    );
    return;
  }

  throw new Http301Error(url);
}
